import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scissors, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DEMO = [
  { label: "Admin / Owner", email: "aryphonecpk.pwk@gmail.com", password: "bordir123" },
  { label: "Kasir", email: "kasir@bordir.id", password: "kasir123" },
  { label: "Designer", email: "desainer@bordir.id", password: "desainer123" },
  { label: "Operator", email: "operator@bordir.id", password: "operator123" },
];

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) { toast.success("Berhasil masuk"); navigate("/"); }
    else toast.error(res.error || "Gagal masuk");
  };

  const quick = (d) => { setEmail(d.email); setPassword(d.password); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between bg-[#0F172A] p-12 overflow-hidden">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -left-20 bottom-10 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
            <Scissors className="h-6 w-6" />
          </div>
          <span className="font-heading text-lg font-extrabold text-white">Bordir<span className="text-blue-400">Kom</span></span>
        </div>
        <div className="relative">
          <h1 className="font-heading text-4xl font-extrabold leading-tight text-white">
            Kelola Usaha<br />Bordir Komputer<br /><span className="text-blue-400">dalam satu sistem.</span>
          </h1>
          <p className="mt-5 max-w-md text-slate-400 leading-relaxed">
            Order & SPK, kalkulator harga stitch, antrean produksi kanban, stok benang,
            hingga nota siap cetak — semua terintegrasi.
          </p>
        </div>
        <p className="relative text-xs text-slate-500">© 2026 BordirKom · Manajemen Usaha Bordir</p>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center bg-[#F8FAFC] p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
              <Scissors className="h-6 w-6" />
            </div>
            <span className="font-heading text-lg font-extrabold text-slate-900">BordirKom</span>
          </div>
          <h2 className="font-heading text-2xl font-extrabold text-slate-900">Selamat datang kembali</h2>
          <p className="mt-1 text-sm text-slate-500">Masuk untuk mengelola workshop bordir Anda.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="input-login-email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="nama@bordir.id" required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Kata Sandi</Label>
              <Input id="password" data-testid="input-login-password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
            </div>
            <Button data-testid="btn-login-submit" type="submit" disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 h-11">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
            </Button>
          </form>

          <div className="mt-7">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Login cepat (demo)</p>
            <div className="grid grid-cols-2 gap-2">
              {DEMO.map((d) => (
                <button key={d.email} type="button" data-testid={`btn-demo-${d.label.split(" ")[0].toLowerCase()}`}
                  onClick={() => quick(d)}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs font-medium text-slate-700 hover:border-blue-400 hover:bg-blue-50 transition-colors">
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
